import { useEffect } from 'react'
import { useParams, useNavigate, Navigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Card, SearchableDropdown, Skeleton } from '@/components/ui'
import { PaymentFields } from '@/components/payments/PaymentFields'
import { usePayment } from '@/hooks/usePayments'
import { useAuth } from '@/hooks/useAuth'
import { useNotificationStore } from '@/stores/useNotificationStore'

/**
 * EP-82 / T149-088: edit a payment from the payments page (/payments/:id/edit).
 *
 * The create branch this page used to have (an invoice picker + PaymentFields,
 * reached at /payments/new) was merged into QuickPayFormPage
 * (/payments/record), which now offers the same invoice picker alongside the
 * standalone/no-invoice fields. /payments/new redirects there.
 *
 * Admin+ only. Staff are redirected to /payments (the Edit affordance is
 * also hidden for staff on the list/detail pages).
 */
export function PaymentFormPage() {
  const { t } = useTranslation('payments')
  const { id } = useParams<{ id: string }>()
  const paymentId = Number(id)
  const navigate = useNavigate()
  const { user } = useAuth()
  const addToast = useNotificationStore((s) => s.addToast)

  const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner'

  const { data: editPayment, isLoading } = usePayment(paymentId)

  // Admin+ guard (staff who type the URL get bounced back to the list).
  useEffect(() => {
    if (user && !isAdminOrOwner) addToast({ type: 'error', message: t('errors.admin_only') })
  }, [user, isAdminOrOwner, addToast, t])
  if (user && !isAdminOrOwner) return <Navigate to="/payments" replace />

  const title = t('actions.edit_payment')

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: t('title'), href: '/payments' }, { label: title }]} />
      <PageHeader title={title} />

      <Card>
        {isLoading ? (
          <Skeleton className="h-64" />
        ) : editPayment ? (
          <div className="space-y-4">
            <SearchableDropdown
              label={t('form.invoice')}
              value={String(editPayment.invoice_id)}
              options={[
                {
                  value: String(editPayment.invoice_id),
                  label: editPayment.invoice_number ?? `#${editPayment.invoice_id}`,
                },
              ]}
              onChange={() => {}}
              disabled
            />
            <PaymentFields
              payment={editPayment}
              invoiceId={editPayment.invoice_id}
              onSave={(p) => navigate(`/payments/${p.id}`)}
            />
          </div>
        ) : (
          <p className="py-8 text-center text-base-content/60">{t('not_found')}</p>
        )}
      </Card>
    </div>
  )
}
